import { useCallback, useEffect, useRef } from "react";

import { createUserRaisedTheirHandToast } from "components/shared/toasts";
import { useAppDispatch, useAppSelector, useArraySelector } from "hooks/redux_hooks";
import { useUsersProperties } from "hooks/use_user_properties";
import { visitBreakoutRoom } from "shared/reducers/classroom";
import { BreakoutRoomType, SidebarPanelType } from "shared/types";
import { setChatTextAreaContentOverride, setSidebarPanel } from "store/reducers/local";
import {
  selectActiveBreakoutSessionId,
  selectBreakoutRoomsForActiveBreakout,
  selectVisitingBreakoutRoomForCurrentUser,
} from "store/selectors/classroom";
import { selectCurrentUserId } from "store/selectors/local";
import { playHandRaiseAudio } from "utils/audio";
import { longDisplayName } from "utils/user";

function HandRaiseObserver() {
  const dispatch = useAppDispatch();
  const currentUserId = useAppSelector(selectCurrentUserId());
  const activeBreakoutSessionId = useAppSelector(selectActiveBreakoutSessionId());
  const currentUserBreakoutRoom = useAppSelector(selectVisitingBreakoutRoomForCurrentUser());

  const breakoutRoomsForActiveBreakout = useArraySelector(selectBreakoutRoomsForActiveBreakout());
  const users = useUsersProperties(["is_hand_raised"]);
  const userIdsWithHandRaised = Object.values(users)
    .filter((user) => user.is_hand_raised)
    .map((user) => user.id);
  const previousUserIdsWithHandRaised = useRef<string[]>([]);

  const handleVisitBreakoutRoomClick = useCallback(
    (breakoutRoom: BreakoutRoomType) => () => {
      dispatch(visitBreakoutRoom({ userId: currentUserId, breakoutRoomId: breakoutRoom.id }));
    },
    [currentUserId, dispatch],
  );

  const handleMessageClick = useCallback(
    (breakoutRoom: BreakoutRoomType, userId: string) => () => {
      dispatch(
        setChatTextAreaContentOverride({
          text: `@Group ${breakoutRoom.title} @${longDisplayName(users[userId])} `,
        }),
      );
      dispatch(setSidebarPanel({ panel: SidebarPanelType.Chat }));
    },
    [dispatch, users],
  );

  useEffect(() => {
    if (!activeBreakoutSessionId) {
      for (const userId of userIdsWithHandRaised) {
        if (!previousUserIdsWithHandRaised.current.includes(userId)) {
          playHandRaiseAudio();
        }
      }
      previousUserIdsWithHandRaised.current = userIdsWithHandRaised;
      return;
    }
    for (const userId of userIdsWithHandRaised) {
      if (!previousUserIdsWithHandRaised.current.includes(userId)) {
        const breakoutRoom = breakoutRoomsForActiveBreakout.find(
          (room) =>
            room.assigned_user_ids?.includes(userId) || room.visiting_user_ids?.includes(userId),
        );
        if (breakoutRoom && breakoutRoom.id !== currentUserBreakoutRoom?.id) {
          window.flightLogger.log(
            `[HandRaiseObserver] User ${userId} raised their hand in breakout room ${breakoutRoom.title}`,
          );
          createUserRaisedTheirHandToast(
            users[userId],
            breakoutRoom.title,
            handleVisitBreakoutRoomClick(breakoutRoom),
            handleMessageClick(breakoutRoom, userId),
          );
        } else if (breakoutRoom && breakoutRoom.id === currentUserBreakoutRoom?.id) {
          playHandRaiseAudio();
        }
      }
    }
    previousUserIdsWithHandRaised.current = userIdsWithHandRaised;
    // We only want to run this effect when the list of users with raised hands changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIdsWithHandRaised]);

  return null;
}

export { HandRaiseObserver };
